import { ViewContainerRef } from '@angular/core';
import { ForceAny } from '@shared/types';
import { ExtendedString } from '@shared/utils/util.types';

import { Message } from 'primeng/api';
import { ToastPositionType } from 'primeng/toast';

/**
 * @type ToastWithTranslateData
 * @property {string} translateKey Translation key whose value will be fetched from translation files.
 * @property {string} priorityMessage Prioritized message over translated message, usually server messages.
 */
export interface ToastWithTranslateData {
  translateKey: string;
  priorityMessage: string;
}

/** Possible values for horizontalPosition. */
export type ToastHorizontalPosition =
  | 'start'
  | 'center'
  | 'end'
  | 'left'
  | 'right';
/** Possible values for verticalPosition on ToastConfig. */
export type ToastVerticalPosition = 'top' | 'bottom';

/**
 * Configuration used when opening a snack-bar.
 */
export class ToastConfig<D> {
  /** The politeness level for the AriaLiveAnnouncer announcement. */
  politeness?: AriaLivePoliteness;
  /**
   * Message to be announced by the LiveAnnouncer. When opening a toast without a custom
   * component or template, the announcement message will default to the specified message.
   */
  announcementMessage?: string;
  /**
   * The view container that serves as the parent for the toast for the purposes of dependency
   * injection. Note: this does not affect where the toast is inserted in the DOM.
   */
  viewContainerRef?: ViewContainerRef;
  /** The length of time in milliseconds to wait before automatically dismissing the snack bar. */
  duration?: number;
  /** Extra CSS classes to be added to the snack bar container. */
  panelClass?: string | string[];
  /** Text layout direction for the snack bar. */
  direction?: Direction;
  /** Data being injected into the child component. */
  data?: D | null;
  /** The horizontal position to place the snack bar. */
  horizontalPosition?: ToastHorizontalPosition;
  /** The vertical position to place the snack bar. */
  verticalPosition?: ToastVerticalPosition;
}

export type AriaLivePoliteness = 'off' | 'polite' | 'assertive';

export type Direction = 'ltr' | 'rtl';

export type MessagePosition =
  | 'top-right'
  | 'top-left'
  | 'bottom-left'
  | 'bottom-right'
  | 'top-center'
  | 'bottom-center'
  | 'center';

/**
 * @interface MessageToastConfig - due to already implemented code,
 * many people are using the old method of opening confirmation toast, unfortunately,
 * I forcefully give support for the string - :(
 * Humble Request to you guys, please use the new method.
 */
export interface MessageToastConfig extends Message {
  key?: ExtendedString<TOAST_KEYS>;
  position?: ExtendedString<ToastPositionType>;
  severity?: ExtendedString<SeverityTypes>;
  hideCancel?: boolean;
  showAsHTML?: boolean;
  styleClassName?: string;
  labels?: {
    confirm: string;
    reject: string;
  };

  autoClose?: {
    duration: number;
    message: string;
  };
  onConfirm?: (event: ForceAny) => void;
  onReject?: (event: ForceAny) => void;
}

export type SeverityTypes = 'success' | 'info' | 'warn' | 'error';

export enum TOAST_KEYS {
  default = 'default',
  confirm = 'confirm', // OLD Method - Please do not use
  CONFIRM = 'CONFIRM', // Injected in root component
}
